import { color } from "d3";
import { getRegionMesh, drawRegions, colorMap } from "./ProteinsBlueprint";
import ProteinRegion from "../../../../models/ProteinRegion";
import packLargeRegions from "../../helpers/packRegions/packLargeRegions";
import { whiteColor } from "../../../../common/helpers/colors";
import Color from "../../models/Color";

function sortLen(a, b) {
  return b.region.size - a.region.size;
}

const DISABLED_REGION_TYPES = new Set(["homo_sapiens proteome sequences"]);
const UNIPROT_REGIONS = "UNIPROT UniProt Regions";

function getDefaultState() {
  return {
    regions: [],
    config: {},
    regionDict: new Map(),
    typeToColor: new Map(),
    floorHeight: 12,
    visibleFloors: 0,
    floors: [],
    visRange: [0, 1],
  };
}

function proteinRegionsController(track) {
  const { loadPosition, requestRender, setHeight } = track;
  const regionMesh = getRegionMesh();
  let state = getDefaultState();

  function onReset() {
    state = getDefaultState();
  }

  function onRangeChange(props) {
    const { chrSegments, start, end } = props;
    state.visRange = [start, end];
    loadPosition(chrSegments);
  }

  function deduplicate(regions) {
    return regions.filter((x) => {
      const isNovel = !state.regionDict.get(x.id);
      if (isNovel) {
        state.regionDict.set(x.id, x);
      }
      return isNovel;
    });
  }

  function appendNewColors(regions) {
    regions
      .filter((x) => !state.typeToColor.get(x.type))
      .map((x) => {
        const [r, g, b] = x.colour.split(",").map((i) => Number(i));
        const rgb = color(whiteColor);
        rgb.r = r;
        rgb.g = g;
        rgb.b = b;
        return state.typeToColor.set(x.type, { rgb });
      });
  }

  function onDataReceived({ chr, res }) {
    const uniprotRegionsEntries = (res || {})[UNIPROT_REGIONS] || {};
    const newRegionData = []
      .concat(...Object.values(uniprotRegionsEntries))
      .filter((x) => x && !DISABLED_REGION_TYPES.has(x.type));

    const newRegions = newRegionData.map((x) => new ProteinRegion(x, chr));

    const uniqueRegions = deduplicate(newRegions);
    const sortedUniqueRegions = uniqueRegions.sort(sortLen);

    appendNewColors(newRegionData);
    packLargeRegions(sortedUniqueRegions, state.floors, 1);

    state.regions = state.regions.concat(sortedUniqueRegions);
    regionMesh.setItems(state.regions);
    updateColorMap();
    drawRegions(sortedUniqueRegions, regionMesh, state.typeToColor);
    calcFloorHeight();
    render();
  }

  function updateColorMap() {
    const { isDark } = state.config;
    const theme = isDark ? 2 : 1;

    Array.from(state.typeToColor.values()).forEach((x, i) => {
      colorMap[i] = new Color(x.rgb).fixLuminance(theme).vec;
      x.indx = i;
    });
    const vecs = colorMap;

    regionMesh.uniforms.setColorMap(vecs);
    render();
  }

  function onPreferencesChanged() {
    state.config = track.getPreferences();
    updateColorMap();
  }

  function render() {
    const floor = state.visibleFloors + 1;
    regionMesh.uniforms.setMaxY(floor);
    setHeight(floor * state.floorHeight);
    requestRender();
  }

  function calcFloorHeight() {
    const start = state.visRange[0];
    const end = state.visRange[1];
    let maxLevel = 0;

    for (let i = 0; i < state.regions.length; i++) {
      const pRegion = state.regions[i];
      const isVisible = pRegion.region.hasOverlap(start, end);

      if (isVisible) {
        maxLevel = Math.max(maxLevel, pRegion.level);
      }
    }
    state.visibleFloors = maxLevel;
    render();
  }

  function onRangeStabilized() {
    calcFloorHeight();
  }

  return {
    onReset,
    onRangeChange,
    onDataReceived,
    onPreferencesChanged,
    onRangeStabilized,
    meshes: [regionMesh],
  };
}

export default proteinRegionsController;
